import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import toast from 'react-hot-toast';

import { meetingService } from '../services/meetingService';
import videoService, { StartVideoSessionResponse } from '../services/videoService';

/**
 * Holds the therapist's active video session.
 *
 * Lives above the router so a call survives navigation: the therapist opens the
 * room and then keeps working — patient page, calendar, summary — while the
 * floating window stays put. A routed page could not do this, because navigating
 * away would unmount it and drop the call.
 */

export type VideoCallStatus = 'starting' | 'live' | 'error';

export interface ActiveVideoCall {
  meetingId: number;
  patientId: number | null;
  patientName: string | null;
  status: VideoCallStatus;
  session: StartVideoSessionResponse | null;
  error?: string;
}

interface VideoCallContextValue {
  activeCall: ActiveVideoCall | null;
  startCall: (meetingId: number) => Promise<void>;
  endCall: () => Promise<void>;
  dismiss: () => void;
  copyPatientLink: () => Promise<void>;
}

const VideoCallContext = createContext<VideoCallContextValue | null>(null);

export const useVideoCall = (): VideoCallContextValue => {
  const ctx = useContext(VideoCallContext);
  if (!ctx) {
    throw new Error('useVideoCall must be used within a VideoCallProvider');
  }
  return ctx;
};

export const VideoCallProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeCall, setActiveCall] = useState<ActiveVideoCall | null>(null);
  // Guards against a second start while one is already in flight, which would
  // otherwise open a room the therapist has no window for.
  const startingRef = useRef(false);

  const startCall = useCallback(async (meetingId: number) => {
    if (startingRef.current) return;
    startingRef.current = true;

    setActiveCall({
      meetingId,
      patientId: null,
      patientName: null,
      status: 'starting',
      session: null,
    });

    try {
      // The video service is not tied to this database's meetings, so it needs the
      // display and scheduling details passed in.
      let therapistName: string | null = null;
      let patientName: string | null = null;
      let scheduledAt: string | null = null;
      let durationMinutes: number | null = null;
      let patientId: number | null = null;
      try {
        const meeting = await meetingService.getMeeting(meetingId);
        therapistName = meeting?.therapist?.full_name ?? null;
        patientName = meeting?.patient?.full_name ?? null;
        scheduledAt = meeting?.scheduled_at ?? null;
        durationMinutes = meeting?.duration_minutes ?? null;
        patientId = meeting?.patient_id ?? meeting?.patient?.id ?? null;
      } catch {
        // Non-fatal: the room still works, the patient page just shows less.
      }

      const session = await videoService.startVideoSession({
        externalId: meetingId,
        therapistDisplayName: therapistName,
        patientDisplayName: patientName,
        scheduledAt,
        durationMinutes,
        locale: 'en',
      });

      setActiveCall({
        meetingId,
        patientId,
        patientName,
        status: 'live',
        session,
      });
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setActiveCall({
        meetingId,
        patientId: null,
        patientName: null,
        status: 'error',
        session: null,
        error: typeof detail === 'string' ? detail : 'Could not start the video session.',
      });
    } finally {
      startingRef.current = false;
    }
  }, []);

  const endCall = useCallback(async () => {
    const sessionId = activeCall?.session?.session_id;
    setActiveCall(null);
    if (sessionId) {
      try {
        await videoService.endVideoSession(sessionId);
      } catch {
        // The room tears itself down once empty, so a failure here is not worth
        // surfacing to the therapist.
      }
    }
  }, [activeCall]);

  const dismiss = useCallback(() => setActiveCall(null), []);

  const copyPatientLink = useCallback(async () => {
    const url = activeCall?.session?.join_url;
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Patient link copied');
    } catch {
      // Clipboard is unavailable outside a secure context or without permission.
      window.prompt('Copy the patient link:', url);
    }
  }, [activeCall]);

  return (
    <VideoCallContext.Provider
      value={{ activeCall, startCall, endCall, dismiss, copyPatientLink }}
    >
      {children}
    </VideoCallContext.Provider>
  );
};

export default VideoCallProvider;
